// ============================================================================
// OBT F3 — Bir yayının cevap listesi (aktörün kapsamındaki cevaplar)
// ============================================================================
// Cevaplar aktörün inceleme kuyruğundan gelir; kapsam backend'de donmuş-zincir
// ile çözülür (başka bölgenin cevabı HİÇ görünmez). Burada ekstra sorgu yok:
// yayın grubundaki `responses` doğrudan gösterilir.
//
// "Cevap gelmedi" parkları reviewer-scoped bir uçla dönmediğinden burada
// UYDURULMAZ (tüm belediye parklarını çekip istemci filtreleme YAPILMAZ) —
// yalnız gerçek cevaplar listelenir.
// ============================================================================

import { Fragment, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ChevronRight, Image, MessageSquare } from 'lucide-react';
import { ObtEmptyState, ObtStatusBadge } from '../ObtStates';
import { RESPONSE_STATUS_LABELS, reviewStateLabel } from './reviewEnums';

const SORTS = Object.freeze([
  { key: 'formen', label: 'Formene göre (A→Z)' },
  { key: 'park', label: 'Park adına göre (A→Z)' },
  { key: 'status', label: 'Duruma göre (önce bekleyen)' },
]);

const trCmp = (a, b) => (a || '').localeCompare(b || '', 'tr');

const FILTERS = Object.freeze([
  { key: 'all', label: 'Tümü' },
  { key: 'pending', label: 'İnceleme Bekliyor' },
  { key: 'resubmitted', label: 'Yeniden Gönderildi' },
]);

function matches(item, filter) {
  if (filter === 'pending') {
    return item.status === 'submitted';
  }
  if (filter === 'resubmitted') {
    return item.status === 'resubmitted';
  }
  return true;
}

/**
 * @param {object} props
 * @param {object} props.publication  {campaign_title, responses[]}
 * @param {Map<string,string>} props.nameById  personnel_id → ad
 * @param {(responseId: string) => void} props.onOpenResponse
 * @param {() => void} props.onBack
 */
export default function ResponseReviewPanel({
  publication,
  nameById,
  onOpenResponse,
  onBack,
}) {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('formen');
  const responses = Array.isArray(publication?.responses) ? publication.responses : [];

  const nameFor = (r) =>
    nameById?.get(String(r.respondent_personnel_id)) || 'Bilinmeyen personel';

  const visible = useMemo(() => {
    const arr = responses.filter((r) => matches(r, filter));
    const statusOrder = { submitted: 0, resubmitted: 1 };
    arr.sort((x, y) => {
      if (sort === 'park') return trCmp(x.park_name, y.park_name);
      if (sort === 'status') {
        const d = (statusOrder[x.status] ?? 9) - (statusOrder[y.status] ?? 9);
        return d !== 0 ? d : trCmp(x.park_name, y.park_name);
      }
      // 'formen' (varsayılan): Formenler kendi aralarında A→Z, her Formenin
      // parkları KENDİ İÇİNDE A→Z (Formen sayısı çoğaldığında karışmaz).
      const d = trCmp(nameFor(x), nameFor(y));
      return d !== 0 ? d : trCmp(x.park_name, y.park_name);
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, filter, sort, nameById]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" /> Yayınlar
        </Button>
        <h3 className="truncate text-base font-semibold text-gray-900">
          {publication?.campaign_title || 'Cevaplar'}
        </h3>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Cevap filtreleri">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="resp-sort" className="text-xs text-gray-500">
            Sırala
          </Label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger id="resp-sort" className="h-8 w-[220px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.key} value={s.key} className="text-xs">
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {visible.length === 0 ? (
        <ObtEmptyState
          title="Cevap bulunmuyor"
          description="Seçili filtreye uyan cevap yok."
        />
      ) : (
        <div className="space-y-2">
          {visible.map((r, idx) => {
            // Formen sıralamasında, Formen değiştiğinde grup başlığı — çok Formenli
            // listede kimin parkı olduğu net kalsın.
            const showFormenHeader =
              sort === 'formen' &&
              (idx === 0 || nameFor(r) !== nameFor(visible[idx - 1]));
            return (
              <Fragment key={String(r.response_id)}>
                {showFormenHeader && (
                  <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Formen: {nameFor(r)}
                  </p>
                )}
                <Card>
              <CardContent className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {r.park_name || 'Park'}
                    </span>
                    <ObtStatusBadge value={r.status} labelMap={RESPONSE_STATUS_LABELS} />
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                      {reviewStateLabel(r.review_state)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span>
                      Formen:{' '}
                      {nameById?.get(String(r.respondent_personnel_id)) ||
                        'Bilinmeyen personel'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" aria-hidden="true" />
                      {r.answer_count} cevap
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Image className="h-3 w-3" aria-hidden="true" />
                      {r.photo_count} foto
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-shrink-0"
                  onClick={() => onOpenResponse(String(r.response_id))}
                >
                  İncele
                  <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Button>
              </CardContent>
                </Card>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
